import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowCounterClockwise, Plus, Trash } from '@phosphor-icons/react';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui-kit/form';
import { FieldLabel } from '../required-fields';
import { cn } from '@/lib/utils';
import { HrInfoValues } from '../../../schemas/employee-form-schema';
import {
  computeAvgDailyWorkHour,
  createScheduleFrom,
  getWorkingInfoDefaults,
  WEEKDAYS,
  WORK_HOUR_OPTIONS,
  WORK_MINUTE_OPTIONS,
  workDaysToday,
} from '../../../schemas/working-info-schema';
import { FIELD_GRID, FormSection } from '../form-layout';

interface ScheduleProps {
  termIndex: number;
  scheduleIndex: number;
}

/** The weekdays of one work-day setting and the hours worked on each of them; read only once locked. */
const ScheduleDays = ({ termIndex, scheduleIndex }: ScheduleProps) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<HrInfoValues>();
  const path = `HrTerms.${termIndex}.WorkSchedules.${scheduleIndex}` as const;
  const isLocked = watch(`${path}.IsLocked`);

  const averageDailyHours = computeAvgDailyWorkHour(
    watch(`${path}.WorkDays`),
    watch(`${path}.WorkHours`)
  );

  const renderTimeSelect = (
    day: (typeof WEEKDAYS)[number],
    unit: 'hour' | 'min',
    isSelected: boolean
  ) => (
    <FormField
      control={control}
      name={`${path}.WorkHours.${day}.${unit}`}
      render={({ field }) => (
        <FormItem className="flex shrink-0 items-center gap-2 space-y-0">
          <Select
            disabled={!isSelected || isLocked}
            value={String(field.value ?? '')}
            onValueChange={(value) => field.onChange(Number(value))}
          >
            <FormControl>
              <SelectTrigger
                className="w-16 sm:w-20"
                aria-label={`${t(day)} ${t(unit === 'hour' ? 'HOUR' : 'MIN')}`}
              >
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {(unit === 'hour' ? WORK_HOUR_OPTIONS : WORK_MINUTE_OPTIONS).map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {t(unit === 'hour' ? 'HOUR_S' : 'MIN_S')}
          </span>
        </FormItem>
      )}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {isLocked && <p className="text-xs text-muted-foreground">{t('WORK_DAYS_SETTING_LOCKED')}</p>}
      <FormField
        control={control}
        name={`${path}.WorkDays`}
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-col divide-y rounded-lg border">
              {WEEKDAYS.map((day) => {
                const isSelected = field.value.includes(day);
                return (
                  <div
                    key={day}
                    className={cn(
                      'flex flex-col gap-2 px-4 py-3 transition-colors sm:flex-row sm:items-center sm:justify-between sm:gap-4',
                      isSelected && 'bg-accent/50'
                    )}
                  >
                    <label
                      className={cn(
                        'flex flex-1 items-center gap-3 text-sm font-medium',
                        !isLocked && 'cursor-pointer'
                      )}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isLocked}
                        onCheckedChange={(checked) =>
                          field.onChange(
                            checked
                              ? [...field.value, day]
                              : field.value.filter((item) => item !== day)
                          )
                        }
                      />
                      {t(day)}
                    </label>

                    {/* Hour and minute side by side on one line; centred in the row on a phone. */}
                    <div className="flex items-center gap-3 max-sm:justify-center max-sm:gap-2">
                      {renderTimeSelect(day, 'hour', isSelected)}
                      {renderTimeSelect(day, 'min', isSelected)}
                    </div>
                  </div>
                );
              })}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className={FIELD_GRID}>
        <div className="space-y-2">
          <Label htmlFor={`avg-daily-work-hour-${termIndex}-${scheduleIndex}`}>
            {t('AVERAGE_WORKING_HOUR_PER_DAY')}
          </Label>
          <Input
            id={`avg-daily-work-hour-${termIndex}-${scheduleIndex}`}
            readOnly
            disabled
            value={averageDailyHours}
          />
        </div>
      </div>
    </div>
  );
};

/** The first day of a change of work days, inside the HR period and after the change before it; on a
 * saved term not before today. A locked change shows its day only. */
const ScheduleStart = ({
  termIndex,
  scheduleIndex,
  onRemove,
}: ScheduleProps & { onRemove: () => void }) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<HrInfoValues>();
  const termStart = watch(`HrTerms.${termIndex}.HrPeriodStartDate`);
  const termEnd = watch(`HrTerms.${termIndex}.HrPeriodEndDate`);
  const isSavedTerm = Boolean(watch(`HrTerms.${termIndex}.ItemId`));
  const isLocked = watch(`HrTerms.${termIndex}.WorkSchedules.${scheduleIndex}.IsLocked`);
  const previousStart =
    scheduleIndex > 1
      ? watch(`HrTerms.${termIndex}.WorkSchedules.${scheduleIndex - 1}.EffectiveFrom`)
      : termStart;
  const today = workDaysToday();
  const earliest = isSavedTerm && (!previousStart || previousStart < today) ? today : previousStart;

  return (
    <div className="flex items-end gap-2">
      <FormField
        control={control}
        name={`HrTerms.${termIndex}.WorkSchedules.${scheduleIndex}.EffectiveFrom`}
        render={({ field }) => (
          <FormItem className="flex-1 sm:max-w-xs">
            <FieldLabel required={!isLocked}>{t('WORK_DAYS_EFFECTIVE_FROM')}</FieldLabel>
            <FormControl>
              <DatePicker
                placeholder={t('SELECT_DATE')}
                min={earliest || undefined}
                max={termEnd || undefined}
                disabled={isLocked}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {!isLocked && (
        <Button
          type="button"
          variant="ghost"
          size="icon-lg"
          onClick={onRemove}
          aria-label={t('REMOVE_WORK_DAYS_CHANGE')}
        >
          <Trash className="size-4" />
        </Button>
      )}
    </div>
  );
};

/**
 * The working days of one HR term. The first setting applies from the start of the term; changing the
 * work days later in the term adds a change from the day it applies, so the days before it keep the work
 * days they were counted with (leave, attendance, TOIL).
 */
export const WorkingDaysSection = ({ termIndex }: { termIndex: number }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue, clearErrors, watch } = useFormContext<HrInfoValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `HrTerms.${termIndex}.WorkSchedules`,
  });
  // A setting already in force can't be wiped by Reset either.
  const hasLocked = (watch(`HrTerms.${termIndex}.WorkSchedules`) ?? []).some(
    (schedule) => schedule.IsLocked
  );

  const handleReset = () => {
    setValue(`HrTerms.${termIndex}.WorkSchedules`, getWorkingInfoDefaults().WorkSchedules, {
      shouldDirty: true,
    });
    clearErrors(`HrTerms.${termIndex}.WorkSchedules`);
  };

  const handleAddChange = () => {
    const schedules = getValues(`HrTerms.${termIndex}.WorkSchedules`);
    append(createScheduleFrom(schedules[schedules.length - 1]));
  };

  return (
    <div className="flex flex-col gap-6">
      <FormSection
        title={t('WORK_DAYS')}
        required
        action={
          !hasLocked && (
            <Button type="button" variant="ghost" size="sm" onClick={handleReset}>
              <ArrowCounterClockwise />
              {t('RESET')}
            </Button>
          )
        }
      >
        {fields.map((field, scheduleIndex) => (
          <div
            key={field.id}
            className={cn('flex flex-col gap-4', scheduleIndex > 0 && 'border-t pt-4')}
          >
            {scheduleIndex === 0 ? (
              fields.length > 1 && (
                <p className="text-sm font-medium">{t('WORK_DAYS_FROM_TERM_START')}</p>
              )
            ) : (
              <ScheduleStart
                termIndex={termIndex}
                scheduleIndex={scheduleIndex}
                onRemove={() => remove(scheduleIndex)}
              />
            )}
            <ScheduleDays termIndex={termIndex} scheduleIndex={scheduleIndex} />
          </div>
        ))}

        <div className="flex flex-col items-start gap-1">
          <Button type="button" variant="outline" size="sm" onClick={handleAddChange}>
            <Plus />
            {t('ADD_WORK_DAYS_CHANGE')}
          </Button>
          <p className="text-xs text-muted-foreground">{t('ADD_WORK_DAYS_CHANGE_HINT')}</p>
        </div>
      </FormSection>
    </div>
  );
};
